import { GLOBAL_SEARCH_GROUP_LIMIT } from "@frontend-shared/constants";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SearchResults } from "../SearchResults";
import { SemanticSearchResults } from "../SemanticSearchResults";
import type { IGlobalSearchChatsSectionProps } from "./types";

// NOTE: While searching, the chat list is capped like every other group, so
// nothing may page it further in behind the cap.
const NO_LOAD_MORE = (): void => undefined;

// NOTE: The two chat sections of the old SearchChats dialog, unchanged in
// behaviour and test ids. With an empty box they are the whole palette (browse
// mode, infinitely scrolled); with a query they are two groups among many,
// capped at GLOBAL_SEARCH_GROUP_LIMIT, and the title section's empty state only
// speaks for the palette when nothing else matched either.
const GlobalSearchChatsSectionComponent = ({
  state,
  isSearching,
  hasOtherResults,
}: IGlobalSearchChatsSectionProps): JSX.Element | null => {
  const { t } = useTranslation("chat");
  const chats = useMemo(
    () =>
      isSearching
        ? state.chats.slice(0, GLOBAL_SEARCH_GROUP_LIMIT)
        : state.chats,
    [isSearching, state.chats],
  );
  const hits = useMemo(
    () => (state.semanticHits ?? []).slice(0, GLOBAL_SEARCH_GROUP_LIMIT),
    [state.semanticHits],
  );
  const isTitleSectionHidden =
    hasOtherResults &&
    chats.length === 0 &&
    !state.isTitleLoading &&
    !state.isTitleError;

  return (
    <>
      {!isTitleSectionHidden && (
        <div
          className="mt-3 first:mt-0"
          data-testid="search-chats-section-titles"
        >
          <h3
            className="truncate px-3 pb-1.5 pt-2 text-xs font-medium text-foreground-secondary"
            data-testid="search-chats-section-titles-header"
          >
            {t("search.section_chats")}
          </h3>
          <SearchResults
            isLoading={state.isTitleLoading}
            isError={state.isTitleError}
            isBelowMinLength={state.isQueryBelowMinLength}
            chats={chats}
            isFetchingNextPage={state.isFetchingNextPage}
            onRetry={state.onRetry}
            onSelectChat={state.onSelectChat}
            loadMoreRef={isSearching ? NO_LOAD_MORE : state.loadMoreRef}
          />
        </div>
      )}
      {state.hasSemanticSection && (
        <div
          className="mt-3 first:mt-0"
          data-testid="search-chats-section-messages"
        >
          <h3
            className="truncate px-3 pb-1.5 pt-2 text-xs font-medium text-foreground-secondary"
            data-testid="search-chats-section-messages-header"
          >
            {t("search.section_messages")}
          </h3>
          <SemanticSearchResults
            isLoading={state.isSemanticLoading}
            isError={state.isSemanticError}
            hits={hits}
            onRetry={state.onRetry}
            onSelectHit={state.onSelectHit}
          />
        </div>
      )}
    </>
  );
};

export const GlobalSearchChatsSection = memo(GlobalSearchChatsSectionComponent);
